import React, { useState, useEffect, useRef } from "react";

import { useOutletContext, useNavigate } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useZustandStore from "zustandStore";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import {
  Grid,
  Box,
  Typography,
  Tooltip,
  ClickAwayListener,
  Popper,
  IconButton,
} from "@mui/material";

import infoTipIcon from "assets/images/icons/icn_Info_solid.svg";
import infoTipHoverIcon from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_table_cell_Remove.svg";
import { ReactComponent as ProIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ModalContainer from "components/ModalContainer";
import InputField from "components/InputField/index.js";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton.js";
import { MenuSelect } from "components/Select/menuSelect.js";
import LinkModal from "components/LinkModal";

import ModalforCheck from "../modalCheck.js";
import ModalForAddOrgOrSite from "../ModalForAddOrgOrSite.js";

import { mergeOrgData } from "utils/getHierarchy";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import { getCountryList, getTimezoneList } from "utils/timezone";

const cloneLabelStyle = {
  fontSize: "14px",
  fontWeight: 700,
  color: "#34456D",
  mb: "5px",
};

const cloneHelperStyle = {
  mt: "4px",
  color: "#939AA8",
  fontSize: "12px",
  lineHeight: "16px",
};

const summaryLabelStyle = {
  color: "#7B8495",
  fontSize: "12px",
  lineHeight: "17px",
};

const summaryValueStyle = {
  color: "#172D6B",
  fontSize: "12px",
  lineHeight: "17px",
  fontWeight: 700,
  wordBreak: "break-word",
};

const CloneInputField = (props) => (
  <Box
    sx={{
      "& > div": {
        margin: "5px 0",
      },
      "& > p": {
        marginLeft: 0,
      },
    }}
  >
    <InputField {...props} />
  </Box>
);

const passwordRequirementsTip = (
  <Box sx={{ fontSize: "14px", lineHeight: 1.5 }}>
    <Typography sx={{ fontSize: "14px", fontWeight: 700, mb: 0.5 }}>
      Password Requirements
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li>8–30 characters</li>
      <li>At least 1 uppercase letter (A–Z)</li>
      <li>At least 1 lowercase letter (a–z)</li>
      <li>At least 1 number (0–9)</li>
      <li>At least 1 special character (~ ! @ # $ ^ &amp; * &#123; &#125; [ ] ( ) _ - + = . | : ; &apos; &quot; &lt; , &gt; / \)</li>
      <li>No consecutive identical characters (e.g., 11, aa, 22)</li>
    </Box>
  </Box>
);

const Site = () => {
  const { scopeData, setScopeData, scopeReady } = useOutletContext();
  const { bread, setBread } = useBreadStorageContext();
  const isSurveillance =
    localStorage.getItem("selectedSystem") === "surveillance";

  const navigate = useNavigate();

  const { dataMap, sendRequest } = useDataSourceManager();
  const [orgSiteUuid] = useState(uuidv4());
  const [clonePlanUuid] = useState(uuidv4());
  const [cloneSiteUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState("");
  const [clonePlan, setClonePlan] = useState(null);
  const [clonePlanLoading, setClonePlanLoading] = useState(false);
  const [cloneCreateLoading, setCloneCreateLoading] = useState(false);
  const [cloneCreateResult, setCloneCreateResult] = useState(null);
  const [openModalForSite, setOpenModalForSite] = useState(false);
  const [openAddSiteAction, setOpenAddSiteAction] = useState(false);
  const [openCloneSiteModal, setOpenCloneSiteModal] = useState(false);
  const [cloneStep, setCloneStep] = useState(1);
  const [cloneSourceSite, setCloneSourceSite] = useState("");
  const [selectedCloneSite, setSelectedCloneSite] = useState(null);
  const [showClonePassword, setShowClonePassword] = useState(false);
  const [refreshingSiteList, setRefreshingSiteList] = useState(false);
  const [siteUnityProMap, setSiteUnityProMap] = useState({});
  const [siteLicenseLoading, setSiteLicenseLoading] = useState(false);

  const siteLicenseRequestMapRef = useRef({});
  const [cloneForm, setCloneForm] = useState({
    newSiteName: "",
    countryIndex: "",
    timeZone: "",
    ntpServers: [""],
    address: "",
    username: "",
    password: "",
  });

  const addSiteButtonRef = useRef(null);
  const [modalForSiteTitle, setModalForSiteTitle] = useState("");
  const [modalForSiteCancelBtn, setModalForSiteCancelBtn] = useState("");
  const [modalForSiteSaveBtn, setModalForSiteSaveBtn] = useState("");
  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState("");
  const [modalCheckContent, setModalCheckContent] = useState("");
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState("");
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState("");
  const [modalFrom, setModalFrom] = useState("");
  const [enableAction, setEnableAction] = useState(false);
  const [usernameError, setUsernameError] = useState("");

  const [passwordError, setPasswordError] = useState("");

  const RESERVED_USERNAMES = [
    "root",
    "daemon",
    "ftp",
    "network",
    "nobody",
    "dnsmasq",
  ];

  const ALLOWED_SPECIAL_CHARS = `~!@#$^&*{}[]()_-+=.|:;"'<,>/`;

  const isValidIPv4 = (value) => {
    const ipv4Regex =
      /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

    return ipv4Regex.test(value);
  };

  const validateUsername = (username) => {
    if (!username) {
      return "Username is required.";
    }

    if (username.length < 1 || username.length > 32) {
      return "Username must be 1–32 characters.";
    }

    if (/\s/.test(username)) {
      return "Username cannot contain spaces.";
    }

    if (username.includes("?")) {
      return 'Username cannot contain "?".';
    }

    if (RESERVED_USERNAMES.includes(username.toLowerCase())) {
      return "This username is reserved and cannot be used.";
    }

    return "";
  };

  const validatePassword = (password, username) => {
    if (!password) {
      return "Password is required.";
    }

    if (password.length < 8 || password.length > 30) {
      return "Password must be 8–30 characters.";
    }

    if (/\s/.test(password)) {
      return "Password cannot contain spaces.";
    }

    if (password.includes("?")) {
      return 'Password cannot contain "?".';
    }

    if (username && password === username) {
      return "Password cannot be the same as username.";
    }

    if (password.toLowerCase() === "admin") {
      return 'Password cannot be "admin".';
    }

    if (isValidIPv4(password)) {
      return "Password cannot be a valid IPv4 address.";
    }

    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter.";
    }

    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter.";
    }

    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number.";
    }

    const specialCharRegex = /[~!@#$^&*{}\[\]()_\-+=.|:;"'<,>\/]/;

    if (!specialCharRegex.test(password)) {
      return `Password must contain at least one special character: ${ALLOWED_SPECIAL_CHARS}`;
    }

    // 只允許英文字母、數字、指定特殊字元
    const allowedCharsRegex = /^[A-Za-z0-9~!@#$^&*{}\[\]()_\-+=.|:;"'<,>\/]+$/;

    if (!allowedCharsRegex.test(password)) {
      return "Password contains an unsupported character.";
    }

    // 任兩個連續相同字元都失敗
    if (/(.)\1/.test(password)) {
      return "Password cannot contain consecutive repeated characters.";
    }

    return "";
  };

  const safeParseLocalStorage = (key, fallback = null) => {
    try {
      const value = localStorage.getItem(key);

      if (!value) {
        return fallback;
      }

      return JSON.parse(value);
    } catch (error) {
      console.error(`Failed to parse localStorage "${key}":`, error);

      return fallback;
    }
  };

  const supportSession = safeParseLocalStorage("supportSession", null);
  const accessLevel = supportSession?.accessLevel;
  const isSupportMode =
    accessLevel === "full_control" || accessLevel === "read_only";
  const isSupporter = safeParseLocalStorage("is_supporter", false);
  const siteData = Array.isArray(scopeData?.site) ? scopeData.site : [];
  const countryOptions = getCountryList();

  const timeZoneOptions =
    cloneForm.countryIndex !== ""
      ? getTimezoneList(cloneForm.countryIndex)
      : [];

  const selectedCountry = countryOptions.find(
    (item) => String(item.value) === String(cloneForm.countryIndex),
  );

  const selectedCountryName = selectedCountry?.label || "-";

  const selectedTimezone = timeZoneOptions.find(
    (item) => item.value === cloneForm.timeZone,
  );

  const selectedTimezoneName =
    selectedTimezone?.label || cloneForm.timeZone || "-";

  const getDatas = async () => {
    setNowUuid(orgSiteUuid);

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    let tmpResource = "";

    let tmpResourceAPIval = {};

    if (Object.keys(tmpSelectedBread.orgs || {}).length === 0) {
      tmpResource = "/orgs/:orgId/sites.read";

      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
      };
    } else {
      tmpResource = "/ents/:entId/orgs/:orgId/sites.read";

      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,

        orgId: tmpSelectedBread.orgs.id,
      };
    }

    sendRequest(tmpResource, orgSiteUuid, tmpResourceAPIval);
  };
  const handleAddItem = () => {
    if (!enableAction) {
      return;
    }

    setModalFrom("Add");
    setModalForSiteTitle("Add New Site");
    setModalForSiteCancelBtn("Cancel");
    setModalForSiteSaveBtn("Save");
    setOpenModalForSite(true);
  };

  const resetCloneForm = () => {
    setCloneStep(1);
    setCloneSourceSite("");
    setSelectedCloneSite(null);
    setClonePlan(null);
    setClonePlanLoading(false);
    setCloneCreateLoading(false);
    setCloneCreateResult(null);
    setShowClonePassword(false);
    setUsernameError("");
    setPasswordError("");
    setCloneForm({
      newSiteName: "",
      countryIndex: "",
      timeZone: "",
      ntpServers: [""],
      address: "",
      username: "",
      password: "",
    });
  };
  const handleCloneSite = () => {
    setOpenAddSiteAction(false);

    resetCloneForm();

    setOpenCloneSiteModal(true);
  };
  useEffect(() => {
    if (!openCloneSiteModal || siteData.length === 0) {
      return;
    }

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    // 每次重新開 Clone Modal 時重新取得
    setSiteUnityProMap({});
    setSiteLicenseLoading(true);

    // 清掉上一次 request
    siteLicenseRequestMapRef.current = {};

    siteData.forEach((site) => {
      const requestUuid = uuidv4();

      let tmpResource = "";
      let tmpResourceAPIval = {};

      if (Object.keys(tmpSelectedBread.orgs || {}).length === 0) {
        tmpResource = "/orgs/:orgId/sites/:siteId/license/service/status.read";

        tmpResourceAPIval = {
          orgId: tmpSelectedBread.realm.id,
          siteId: site.id,
        };
      } else {
        tmpResource =
          "/ents/:entId/orgs/:orgId/sites/:siteId/license/service/status.read";

        tmpResourceAPIval = {
          entId: tmpSelectedBread.realm.id,
          orgId: tmpSelectedBread.orgs.id,
          siteId: site.id,
        };
      }

      siteLicenseRequestMapRef.current[requestUuid] = String(site.id)

      sendRequest(tmpResource, requestUuid, tmpResourceAPIval);
    });
  }, [openCloneSiteModal]);

  useEffect(() => {
    const requestMap = siteLicenseRequestMapRef.current;

    const requestEntries = Object.entries(requestMap);

    if (requestEntries.length === 0) {
      return;
    }

    const updates = {};

    requestEntries.forEach(([requestUuid, siteId]) => {
      const response = dataMap[requestUuid];

      if (response === undefined) {
        return;
      }

      delete siteLicenseRequestMapRef.current[requestUuid];

      if (response?.code !== undefined) {
        console.error(
          `[Clone Site] Failed to get Unity Pro status for site ${siteId}:`,
          response,
        );

        return;
      }

      const data = response?.result?.data ?? response?.data ?? response;

      const unityPro = data?.services?.unityPro;

      if (typeof unityPro === "boolean") {
        updates[String(siteId)] = unityPro;
      }
    });

    if (Object.keys(updates).length > 0) {
      setSiteUnityProMap((prev) => ({
        ...prev,
        ...updates,
      }));
    }

    if (Object.keys(siteLicenseRequestMapRef.current).length === 0) {
      setSiteLicenseLoading(false);
    }
  }, [dataMap]);

  const handleCloseCloneSite = () => {
    setOpenCloneSiteModal(false);

    resetCloneForm();
  };

  const handleCloneSourceChange = (option) => {
    const sourceSiteId = option?.value || "";

    setCloneSourceSite(sourceSiteId);
    setClonePlan(null);

    if (!sourceSiteId) {
      setSelectedCloneSite(null);
      setClonePlanLoading(false);
      setUsernameError("");
      setPasswordError("");

      setCloneForm({
        newSiteName: "",
        countryIndex: "",
        timeZone: "",
        ntpServers: [""],
        address: "",
        username: "",
        password: "",
      });

      return;
    }

    const selectedSite = siteData.find(
      (site) => String(site.id) === String(sourceSiteId),
    );

    if (!selectedSite) {
      return;
    }

    setSelectedCloneSite(selectedSite);
    setClonePlanLoading(true);

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    let tmpResource = "";
    let tmpResourceAPIval = {};

    if (Object.keys(tmpSelectedBread.orgs || {}).length === 0) {
      tmpResource = "/orgs/:orgId/sites/:sourceSiteId/clonePlan.read";

      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
        sourceSiteId,
      };
    } else {
      tmpResource =
        "/ents/:entId/orgs/:orgId/sites/:sourceSiteId/clonePlan.read";

      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id,
        sourceSiteId,
      };
    }

    setNowUuid(clonePlanUuid);

    sendRequest(tmpResource, clonePlanUuid, tmpResourceAPIval);
  };
  const handleCountryChange = (option) => {
    const countryIndex = option?.value ?? "";

    if (countryIndex === "") {
      setCloneForm((prev) => ({
        ...prev,

        countryIndex: "",

        timeZone: "",
      }));

      return;
    }

    const timezoneList = getTimezoneList(countryIndex);

    setCloneForm((prev) => ({
      ...prev,

      countryIndex,

      timeZone: timezoneList[0]?.value || "",
    }));
  };

  const handleCountryTypeAhead = (event) => {
    if (
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.key.length !== 1
    ) {
      return;
    }

    const key = event.key.toLocaleLowerCase();

    if (!key.trim()) {
      return;
    }

    const matchedCountry = countryOptions.find((option) =>
      option.label.toLocaleLowerCase().startsWith(key),
    );

    if (!matchedCountry) {
      return;
    }

    event.preventDefault();
    handleCountryChange(matchedCountry);

    window.requestAnimationFrame(() => {
      const countryOption = Array.from(
        document.querySelectorAll(".MuiPopper-root .MuiListItem-root"),
      ).find((element) => element.textContent.trim() === matchedCountry.label);

      countryOption?.scrollIntoView({ block: "nearest" });
    });
  };

  const handleCloneFormChange = (field, value) => {
    setCloneForm((prev) => ({
      ...prev,

      [field]: value,
    }));
  };

  const handleNtpServerChange = (index, value) => {
    setCloneForm((prev) => {
      const nextServers = [...prev.ntpServers];

      nextServers[index] = value;

      return {
        ...prev,

        ntpServers: nextServers,
      };
    });
  };

  const handleAddNtpServer = () => {
    setCloneForm((prev) => {
      if (prev.ntpServers.length >= 3) {
        return prev;
      }

      return {
        ...prev,

        ntpServers: [...prev.ntpServers, ""],
      };
    });
  };

  const handleRemoveNtpServer = (index) => {
    if (index === 0) {
      return;
    }

    setCloneForm((prev) => ({
      ...prev,

      ntpServers: prev.ntpServers.filter((_, i) => i !== index),
    }));
  };

  const handleCloneNext = () => {
    if (!selectedCloneSite || !clonePlan) {
      return;
    }

    const unityPro = siteUnityProMap[String(selectedCloneSite.id)];

    if (unityPro === undefined) {
      return;
    }

    if (unityPro === false) {
      setOpenLinkModal(true);
      return;
    }

    setCloneStep(2);
  };

  const handleCloneBack = () => {
    setCloneStep(1);
  };

  const handleFlashSite = () => {
    if (cloneCreateLoading) {
      return;
    }

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    const cloneData = {
      sourceSiteId: cloneSourceSite,

      name: cloneForm.newSiteName,

      address: cloneForm.address,

      countryDomain: {
        countryIndex: cloneForm.countryIndex,
      },

      timeConfig: {
        timezone: cloneForm.timeZone,

        ntpServer1: cloneForm.ntpServers[0] || "",

        ntpServer2: cloneForm.ntpServers[1] || "",

        ntpServer3: cloneForm.ntpServers[2] || "",
      },

      deviceAccount: {
        username: cloneForm.username,
        password: cloneForm.password,
      },
    };

    let tmpResource = "";
    let restfulPlaceholders = {};

    const requestParams = {
      data: cloneData,
    };

    if (Object.keys(tmpSelectedBread.orgs || {}).length === 0) {
      tmpResource = "/orgs/:orgId/siteClones.create";

      restfulPlaceholders = {
        orgId: tmpSelectedBread.realm.id,
      };
    } else {
      tmpResource = "/ents/:entId/orgs/:orgId/siteClones.create";

      restfulPlaceholders = {
        entId: tmpSelectedBread.realm.id,

        orgId: tmpSelectedBread.orgs.id,
      };
    }

    setCloneCreateLoading(true);

    setCloneCreateResult(null);

    setNowUuid(cloneSiteUuid);

    sendRequest(tmpResource, cloneSiteUuid, restfulPlaceholders, requestParams);
  };
  const handleGoToLicenseManagement = () => {
    setOpenLinkModal(false);
    navigate("/license");
  };
  const handleAddDevices = () => {
    if (!cloneCreateResult?.siteId) {
      return;
    }

    const newSite = siteData.find(
      (site) => String(site.id) === String(cloneCreateResult.siteId),
    );

    if (!newSite) {
      console.error("New cloned site not found:", cloneCreateResult.siteId);

      return;
    }

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    tmpSelectedBread.site = newSite;

    localStorage.setItem("selectBreadcrumbs", JSON.stringify(tmpSelectedBread));

    setBread(tmpSelectedBread);

    setOpenCloneSiteModal(false);

    navigate("/site/devices", {
      state: {
        openDiscover: true,
        siteId: newSite.id,
      },
    });
  };
  const handleViewNewSite = () => {
    if (refreshingSiteList || !cloneCreateResult?.siteId) {
      return;
    }

    const newSite = siteData.find(
      (site) => String(site.id) === String(cloneCreateResult.siteId),
    );

    if (!newSite) {
      console.error("New cloned site not found.");

      return;
    }

    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    tmpSelectedBread.site = newSite;

    localStorage.setItem("selectBreadcrumbs", JSON.stringify(tmpSelectedBread));

    setBread(tmpSelectedBread);

    setOpenCloneSiteModal(false);
    getDatas();
    navigate("/site/dashboard");
  };
  const handleBackToSiteList = () => {
    setOpenCloneSiteModal(false);
    resetCloneForm();
    getDatas();
  };

  const renderAddSiteButton = () => (
    <Box>
      <Box
        ref={addSiteButtonRef}
        sx={{
          display: "inline-flex",
        }}
      >
        <SolidButton
          startIcon={<AddIcon />}
          height="32px"
          onClick={() => setOpenAddSiteAction((open) => !open)}
        >
          Add Site
        </SolidButton>
      </Box>

      <Popper
        open={openAddSiteAction}
        anchorEl={addSiteButtonRef.current}
        placement="bottom-end"
        strategy="fixed"
        modifiers={[
          {
            name: "offset",

            options: {
              offset: [0, 8],
            },
          },
        ]}
        sx={{
          zIndex: 1300,
        }}
      >
        <ClickAwayListener onClickAway={() => setOpenAddSiteAction(false)}>
          <Box
            sx={{
              position: "relative",
              width: "300px",
              bgcolor: "primary.lightBlue",
              px: "20px",
              py: "10px",
              borderRadius: 4,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-8px",
                right: "20px",
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: "8px solid",
                borderBottomColor: "primary.lightBlue",
              },
            }}
          >
            {[
              [
                "Create a Blank Site",

                () => {
                  setOpenAddSiteAction(false);
                  handleAddItem();
                },
              ],

              ["Clone an Existing Site", handleCloneSite],
            ].map(([label, onClick], index) => (
              <Box
                key={label}
                onClick={onClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "260px",
                  height: "50px",
                  px: "10px",
                  color: "primary.main",
                  cursor: "pointer",
                  ...(index === 0 && {
                    borderBottom: "1px solid",
                    borderColor: "primary.borderGreyPrimary",
                  }),

                  "&:hover": {
                    color: "primary.mainHover",
                  },
                }}
              >
                <span>{label}</span>
                {label === "Clone an Existing Site" && (
                  <ProIcon
                    aria-label="Pro feature"
                    style={{ width: "32px", height: "32px", display: "block" }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );

  const handleScopeLink = (item) => {
    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    tmpSelectedBread.site = item;

    localStorage.setItem(
      "selectBreadcrumbs",

      JSON.stringify(tmpSelectedBread),
    );

    setBread(tmpSelectedBread);

    navigatePage();
  };

  const navigatePage = () => {
    const selectedSystem = localStorage.getItem("selectedSystem") || "network";

    const path =
      selectedSystem === "surveillance"
        ? "/surveillance/dashboard"
        : "/site/dashboard";

    navigate(path);
  };

  const checkPermissions = () => {
    const tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    if (!tmpSelectedBread || Object.keys(tmpSelectedBread).length === 0) {
      return;
    }

    if (Object.keys(tmpSelectedBread.orgs || {}).length === 0) {
      if (
        tmpSelectedBread.realm?.roleId === 5 ||
        tmpSelectedBread.realm?.roleId === 6
      ) {
        setEnableAction(true);
      } else {
        setEnableAction(false);
      }
    } else {
      if (
        tmpSelectedBread.orgs?.roleId === 5 ||
        tmpSelectedBread.orgs?.roleId === 6
      ) {
        setEnableAction(true);
      } else {
        setEnableAction(false);
      }
    }
  };

  useEffect(() => {
    checkPermissions();
  }, [bread]);

  useEffect(() => {
    if (scopeReady) {
      checkPermissions();
    }
  }, [scopeReady]);

  const handleActionDenied = (message) => {
    setOpenModalForSite(false);
    setModalCheckTitle("Action denied");
    setModalCheckContent(message);
    setModalCheckCancelBtn("");
    setModalCheckSaveBtn("OK");
    setModalFrom("error");
    setOpenModalCheck(true);
  };
  useEffect(() => {
    if (!nowUuid) {
      return;
    }

    switch (nowUuid) {
      case orgSiteUuid: {
        const response = dataMap[orgSiteUuid];

        if (response === undefined) {
          break;
        }

        if (response?.code !== undefined) {
          setModalCheckTitle("Action denied");

          setModalCheckContent(response.message);

          setModalCheckCancelBtn("");
          setModalCheckSaveBtn("OK");
          setModalFrom("error");
          setOpenModalCheck(true);

          setRefreshingSiteList(false);

          setNowUuid("");

          break;
        }

        const result = mergeOrgData(response);

        setScopeData((prev) => ({
          ...prev,
          site: result,
        }));
        if (refreshingSiteList && cloneCreateResult?.siteId) {
          const newSite = result.find(
            (site) => String(site.id) === String(cloneCreateResult.siteId),
          );

          if (newSite) {
            setRefreshingSiteList(false);
          } else {
            setTimeout(() => {
              getDatas();
            }, 800);

            setNowUuid("");

            break;
          }
        }

        setNowUuid("");

        break;
      }

      case clonePlanUuid: {
        const response = dataMap[clonePlanUuid];

        if (response === undefined) {
          break;
        }

        setClonePlanLoading(false);

        if (response?.code !== undefined) {
          setModalCheckTitle("Action denied");

          setModalCheckContent(
            response.message || "Failed to load clone plan.",
          );

          setModalCheckCancelBtn("");

          setModalCheckSaveBtn("OK");

          setModalFrom("error");

          setOpenModalCheck(true);

          setNowUuid("");

          break;
        }

        const data = response?.result?.data ?? response?.data ?? response;
        const defaults = data?.defaults;

        if (!defaults) {
          console.error("clonePlan defaults not found:", response);
          setModalCheckTitle("Clone site error");
          setModalCheckContent("Unable to load clone plan defaults.");
          setModalCheckCancelBtn("");
          setModalCheckSaveBtn("OK");
          setModalFrom("error");
          setOpenModalCheck(true);
          setNowUuid("");
          break;
        }

        setClonePlan(data);

        const ntpServers = [
          defaults.timeConfig?.ntpServer1 || "",

          defaults.timeConfig?.ntpServer2 || "",

          defaults.timeConfig?.ntpServer3 || "",
        ];

        const normalizedNtpServers = [
          ntpServers[0],

          ...ntpServers.slice(1).filter(Boolean),
        ];

        const nextCloneForm = {
          newSiteName: defaults.suggestedSiteName || "",
          countryIndex: defaults.countryDomain?.countryIndex ?? "",
          timeZone: defaults.timeConfig?.timezone || "",
          ntpServers: normalizedNtpServers,
          address: defaults.address || "",
          username: defaults.deviceAccount?.username || "",
          password: defaults.deviceAccount?.password || "",
        };

        setCloneForm(nextCloneForm);
        setUsernameError(validateUsername(nextCloneForm.username));
        setPasswordError(
          validatePassword(nextCloneForm.password, nextCloneForm.username),
        );

        setNowUuid("");

        break;
      }

      case cloneSiteUuid: {
        const response = dataMap[cloneSiteUuid];

        if (response === undefined) {
          break;
        }

        setCloneCreateLoading(false);

        if (response?.code !== undefined) {
          setModalCheckTitle("Clone site error");
          setModalCheckContent(response?.message || "Failed to create site.");
          setModalCheckCancelBtn("");
          setModalCheckSaveBtn("OK");
          setModalFrom("error");
          setOpenModalCheck(true);

          setNowUuid("");

          break;
        }

        const data = response?.result?.data ?? response?.data ?? response;

        if (!data?.siteId) {
          setModalCheckTitle("Clone site error");
          setModalCheckContent("Site was created but no siteId was returned.");
          setModalCheckCancelBtn("");
          setModalCheckSaveBtn("OK");
          setModalFrom("error");
          setOpenModalCheck(true);
          setNowUuid("");

          break;
        }

        setCloneCreateResult(data);
        setCloneStep(3);
        setRefreshingSiteList(true);
        setNowUuid("");
        getDatas();

        break;
      }

      default:
        break;
    }
  }, [
    dataMap,
    nowUuid,
    orgSiteUuid,
    clonePlanUuid,
    cloneSiteUuid,
    setScopeData,
  ]);

  const columnHelper = createColumnHelper();

  const renderDeviceStatus = (devices) =>
    `${devices?.online ?? 0} | ${devices?.offline ?? 0} | ${devices?.onboarding ?? 0
    }`;

  const renderClients = (clients) =>
    `${clients?.wireless ?? 0} | ${clients?.wired ?? 0}`;

  const renderAlerts = (alerts) =>
    `${alerts?.critical ?? 0} | ${alerts?.normal ?? 0} | ${alerts?.low ?? 0}`;

  const renderCountTooltip = (value, label) => (
    <Tooltip title={label} arrow placement="bottom">
      <Box component="span" sx={{ cursor: "default" }}>
        {value}
      </Box>
    </Tooltip>
  );

  const columnsData = [
    columnHelper.accessor("name", {
      header: "Name",

      cell: (info) => (
        <Box sx={{ display: "flex", minWidth: 0 }}>
          <EllipsisTooltipCell
            text={info.getValue()}
            onClick={() => handleScopeLink(info.row.original)}
            sx={{
              color: "#707ca8",
              cursor: "pointer",
              textDecoration: "underline",
              flex: "1 1 0",
              minWidth: 0,
            }}
          />
        </Box>
      ),
    }),

    columnHelper.accessor("roleName", {
      header: "Role",
    }),

    columnHelper.accessor("country", {
      header: "Country",
    }),

    ...(isSurveillance
      ? [
        columnHelper.accessor("cameras", {
          header: "Camera",
          cell: (info) =>
            renderCountTooltip(
              renderDeviceStatus(info.getValue()),
              "Online | Offline | Onboarding",
            ),
        }),
        columnHelper.accessor("nvrs", {
          header: "NVR",
          cell: (info) =>
            renderCountTooltip(
              renderDeviceStatus(info.getValue()),
              "Online | Offline | Onboarding",
            ),
        }),
      ]
      : [
        columnHelper.accessor("accessPoints", {
          header: "Access Point",
          cell: (info) =>
            renderCountTooltip(
              renderDeviceStatus(info.getValue()),
              "Online | Offline | Onboarding",
            ),
        }),
        columnHelper.accessor("switches", {
          header: "Switches",
          cell: (info) =>
            renderCountTooltip(
              renderDeviceStatus(info.getValue()),
              "Online | Offline | Onboarding",
            ),
        }),
        columnHelper.accessor("routers", {
          header: "Routers",
          cell: (info) =>
            renderCountTooltip(
              renderDeviceStatus(info.getValue()),
              "Online | Offline | Onboarding",
            ),
        }),
      ]),

    ...(!isSurveillance
      ? [
        columnHelper.accessor("clients", {
          header: "Clients",
          cell: (info) =>
            renderCountTooltip(
              renderClients(info.getValue()),
              "Wireless Users | Wire Users",
            ),
        }),
        columnHelper.accessor("alerts", {
          header: "Alerts",
          cell: (info) =>
            renderCountTooltip(
              renderAlerts(info.getValue()),
              "Critical | Normal | Low",
            ),
        }),
      ]
      : []),

    columnHelper.accessor("createdTime", {
      header: "Created Time",
    }),
  ];

  const table = useTableInstance({
    tableName: "hierarchyRealmSite",

    data: siteData,

    columns: columnsData,

    enableSorting: true,

    enablePagination: true,

    enableGlobalFilter: true,

    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmSite",

    data: "",

    columns: columnsData,

    enableSorting: true,
  });

  const usernameValidationError = validateUsername(cloneForm.username);

  const passwordValidationError = validatePassword(
    cloneForm.password,
    cloneForm.username,
  );

  const cloneNextDisabled =
    clonePlanLoading ||
    !selectedCloneSite ||
    !clonePlan ||
    !cloneForm.newSiteName.trim() ||
    cloneForm.countryIndex === "" ||
    !cloneForm.timeZone ||
    !cloneForm.ntpServers[0]?.trim() ||
    Boolean(usernameValidationError) ||
    Boolean(passwordValidationError);
  const cloneSummary = clonePlan?.cloneSummary;

  const wirelessNames = cloneSummary?.wirelessNetworkNames || [];

  const templateLabels = cloneSummary?.configProvisioningTemplateLabels || [];

  const administratorNames = cloneSummary?.administratorNames || [];

  const paidServiceNames = clonePlan?.sourceEnabledPaidServiceNames || [];

  const createdPaidServiceNames =
    cloneCreateResult?.sourceEnabledPaidServiceNames || [];

  const comingWithItems = [
    "General Settings",

    `${wirelessNames.length} Wireless Network(s)${wirelessNames.length ? ` (${wirelessNames.join(", ")})` : ""
    }`,

    "Wired Basic Config",

    `${templateLabels.length} Config Provisioning Template(s)${templateLabels.length ? ` (${templateLabels.join(", ")})` : ""
    }`,

    `${cloneSummary?.captivePortalProfileCount ?? 0} Captive Portal Profile(s)`,

    `${cloneSummary?.portProfileCount ?? 0} Port Profile(s)`,

    `${cloneSummary?.macAddressGroupCount ?? 0} MAC Group(s)`,

    `${cloneSummary?.radiusServerProfileCount ?? 0} RADIUS Profile(s)`,

    `${administratorNames.length} administrator(s)${administratorNames.length ? ` (${administratorNames.join(", ")})` : ""
    } will have access to this site. Their individual notification settings will be copied; email delivery follows those settings.`,
  ];

  const startsEmptyItems = [
    "No devices yet — add them from the Devices page after creation",

    ...(paidServiceNames.length > 0
      ? [
        `Paid services — "${paidServiceNames.join(
          ", ",
        )}" enabled on the source site will not be enabled automatically on the new site. Turn them on from License Management.`,
      ]
      : []),

    "Topology, Clients, and AInsight populate as devices connect",
  ];
  const cloneSiteOptions = siteData.map((site) => ({
    label: site.name,
    value: site.id,
    isUnityPro: siteUnityProMap[String(site.id)],
  }));

  return (
    <>
      <div
        className="main-card-content"
        style={{
          marginTop: "60px",

          flex: siteData.length > 0 ? "" : "1",

          display: "flex",

          flexDirection: "column",
        }}
      >
        <div>
          <Grid
            container
            style={{
              justifyContent: "space-between",

              marginBottom: "10px",
            }}
          >
            <Box
              style={{
                display: "flex",
              }}
            >
              <Grid
                container
                sx={{
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="600"
                  color="rgba(0, 0, 0, 0.8)"
                >
                  Site
                </Typography>
              </Grid>
            </Box>

            <Box
              style={{
                display: "flex",
              }}
            >
              {siteData.length > 0 && (
                <Grid
                  container
                  sx={{
                    alignItems: "center",

                    gap: "15px",
                  }}
                >
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />

                  <Box>
                    {enableAction && (
                      <Box
                        sx={{
                          ...((isSupportMode || isSupporter) && {
                            filter: "grayscale(100%)",

                            opacity: 0.4,

                            pointerEvents: "none",
                          }),
                        }}
                      >
                        {renderAddSiteButton()}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Box>
          </Grid>
        </div>

        {siteData.length > 0 && <TableField {...table} />}

        {siteData.length === 0 && (
          <>
            <TableField {...emptyHead} />

            <div
              style={{
                flex: "1",

                alignContent: "center",

                justifyItems: "center",
              }}
            >
              <Grid
                container
                style={{
                  justifyContent: "center",

                  marginBottom: "40px",
                }}
              >
                <Typography
                  sx={{
                    textAlign: "center",
                  }}
                  fontSize="16px"
                  fontWeight="600"
                  color="rgba(0, 0, 0, 0.8)"
                >
                  No entry in the table, you can create a new site.
                </Typography>
              </Grid>

              <Box>
                {enableAction && (
                  <Box
                    sx={{
                      ...((isSupportMode || isSupporter) && {
                        filter: "grayscale(100%)",

                        opacity: 0.4,

                        pointerEvents: "none",
                      }),
                    }}
                  >
                    <SolidButton
                      startIcon={<AddIcon />}
                      height="32px"
                      onClick={handleAddItem}
                    >
                      Add Site
                    </SolidButton>
                  </Box>
                )}
              </Box>
            </div>
          </>
        )}
      </div>
      <ModalForAddOrgOrSite
        auth="Site"
        display={openModalForSite}
        title={modalForSiteTitle}
        setOpenModal={setOpenModalForSite}
        cancelBtn={modalForSiteCancelBtn}
        saveBtn={modalForSiteSaveBtn}
        errorHandling={handleActionDenied}
        refresh={getDatas}
      />
      <ModalContainer display={openCloneSiteModal} modalWidth="680px">
        <Box
          sx={{
            width: "100%",

            bgcolor: "#FFFFFF",

            maxHeight: "90vh",

            display: "flex",

            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              justifyContent: "space-between",

              px: "20px",

              py: "14px",

              borderBottom: "1px solid #E5E7EB",

              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "#172D6B",

                fontSize: "16px",

                fontWeight: 700,
              }}
            >
              {cloneStep === 3 ? "Site created" : "Site Cloning — Clone Site"}
            </Typography>

            <IconButton
              size="small"
              onClick={handleCloseCloneSite}
              sx={{
                color: "#8E96A8",
              }}
            >
              <CloseIcon
                sx={{
                  fontSize: "20px",
                }}
              />
            </IconButton>
          </Box>

          <Box
            sx={{
              overflowY: "auto",

              flex: 1,
            }}
          >
            {cloneStep !== 3 && (
              <Box
                sx={{
                  px: "20px",

                  pt: "18px",

                  pb: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",

                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: "22px",

                        height: "22px",

                        borderRadius: "50%",

                        bgcolor: cloneStep === 1 ? "#172D6B" : "#14A83B",

                        color: "#FFFFFF",

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        fontSize: "11px",

                        fontWeight: 700,
                      }}
                    >
                      {cloneStep === 2 ? "✓" : "1"}
                    </Box>

                    <Typography
                      sx={{
                        ml: "8px",

                        color: cloneStep === 1 ? "#172D6B" : "#667085",

                        fontSize: "14px",

                        fontWeight: 700,

                        whiteSpace: "nowrap",
                      }}
                    >
                      Name & Destination
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,

                      mx: "12px",

                      height: "2px",

                      bgcolor: cloneStep === 2 ? "#53B36B" : "#E2E5EC",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: "22px",

                        height: "22px",

                        borderRadius: "50%",

                        bgcolor: cloneStep === 2 ? "#172D6B" : "#ECEEF3",

                        color: cloneStep === 2 ? "#FFFFFF" : "#A2A8B3",

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        fontSize: "11px",

                        fontWeight: 700,
                      }}
                    >
                      2
                    </Box>

                    <Typography
                      sx={{
                        ml: "8px",

                        color: cloneStep === 2 ? "#172D6B" : "#858D9D",

                        fontSize: "14px",

                        fontWeight: 700,

                        whiteSpace: "nowrap",
                      }}
                    >
                      Confirm & Flash
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {cloneStep === 1 && (
              <Box
                sx={{
                  px: "20px",

                  pb: "20px",
                }}
              >
                <Typography
                  sx={{
                    color: "#7D8492",

                    fontSize: "13px",

                    lineHeight: "18px",

                    mb: "14px",
                  }}
                >
                  Where is this new site going? Values are pre-filled from the
                  source site — edit anything before you flash.
                </Typography>

                {/* Source Site */}

                <Box
                  sx={{
                    mb: "14px",
                  }}
                >
                  <Typography sx={cloneLabelStyle}>Source Site *</Typography>

                  <MenuSelect
                    value={cloneSourceSite}
                    onChange={handleCloneSourceChange}
                    options={cloneSiteOptions}
                    width="100%"
                    placeholder={
                      siteLicenseLoading
                        ? "Loading sites..."
                        : "Select a site to clone..."
                    }
                    renderOption={(option) => (
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "12px",
                            color: "#172D6B",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.label}
                        </Typography>

                        {option.isUnityPro === true && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <ProIcon
                              style={{
                                width: "60px",
                                height: "60px",
                                display: "block",
                              }}
                            />
                          </Box>
                        )}

                        {option.isUnityPro === false && (
                          <Tooltip
                            title="Pro feature disabled."
                            arrow
                            placement="bottom"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#4D4D4D",
                                  color: "#FFFFFF",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "6px",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "#4D4D4D",
                                },
                              },
                            }}
                          >
                            <Box
                              component="span"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <ProOrangeOutlineIcon
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  display: "block",
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    renderValue={(option) => (
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "12px",
                            color: "#172D6B",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.label}
                        </Typography>

                        {option.isUnityPro === true && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <ProIcon
                              style={{
                                width: "38px",
                                height: "18px",
                                display: "block",
                              }}
                            />
                          </Box>
                        )}

                        {option.isUnityPro === false && (
                          <Tooltip
                            title="Pro feature disabled."
                            arrow
                            placement="bottom"
                          >
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <ProOrangeOutlineIcon
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  display: "block",
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  />
                  <Typography sx={cloneHelperStyle}>
                    Pick the site whose configuration you want to copy.
                  </Typography>

                  {clonePlanLoading && (
                    <Typography
                      sx={{
                        ...cloneHelperStyle,

                        color: "#172D6B",

                        fontWeight: 600,
                      }}
                    >
                      Loading source site defaults...
                    </Typography>
                  )}
                </Box>

                {!selectedCloneSite && (
                  <Box
                    sx={{
                      px: "12px",

                      py: "11px",

                      bgcolor: "#F1F3FF",

                      border: "1px solid #E4E7FF",

                      borderRadius: "5px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#34487F",

                        fontSize: "14px",

                        fontWeight: 500,
                      }}
                    >
                      ↑ Select a source site to load its defaults.
                    </Typography>
                  </Box>
                )}

                {selectedCloneSite && (
                  <>
                    <Box
                      sx={{
                        mb: "14px",
                      }}
                    >
                      <Typography sx={cloneLabelStyle}>
                        New Site Name *
                      </Typography>

                      <CloneInputField
                        value={cloneForm.newSiteName}
                        onChange={(value) =>
                          handleCloneFormChange(
                            "newSiteName",

                            value,
                          )
                        }
                      />

                      <Typography sx={cloneHelperStyle}>
                        Default is the source name + " - Copy".
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",

                        gridTemplateColumns: "1fr 1fr",

                        gap: "12px",

                        mb: "14px",
                      }}
                    >
                      <Box>
                        <Typography sx={cloneLabelStyle}>
                          Country / Region *
                        </Typography>

                        <Box onKeyDown={handleCountryTypeAhead}>
                          <MenuSelect
                            value={cloneForm.countryIndex}
                            onChange={handleCountryChange}
                            options={countryOptions}
                            width="100%"
                            menuWidth="100%"
                            placeholder="Select country"
                          />
                        </Box>
                      </Box>

                      <Box>
                        <Typography sx={cloneLabelStyle}>
                          Time Zone *
                        </Typography>

                        <MenuSelect
                          value={cloneForm.timeZone}
                          onChange={(option) =>
                            handleCloneFormChange(
                              "timeZone",

                              option?.value || "",
                            )
                          }
                          options={timeZoneOptions}
                          width="100%"
                          menuWidth="100%"
                          placeholder={
                            cloneForm.countryIndex === ""
                              ? "Select country first"
                              : "Select timezone"
                          }
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mb: "14px",
                      }}
                    >
                      <Typography sx={cloneLabelStyle}>
                        NTP Servers *
                      </Typography>

                      {cloneForm.ntpServers.map((server, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",

                            alignItems: "center",

                            gap: "7px",

                            mb: "7px",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                            }}
                          >
                            <CloneInputField
                              value={server}
                              onChange={(value) =>
                                handleNtpServerChange(
                                  index,

                                  value,
                                )
                              }
                            />
                          </Box>

                          {index > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveNtpServer(index)}
                            >
                              <RemoveIcon
                                sx={{
                                  fontSize: "20px",
                                }}
                              />
                            </IconButton>
                          )}
                        </Box>
                      ))}

                      <Box
                        sx={{
                          display: "flex",

                          justifyContent: "flex-end",

                          mt: "3px",
                        }}
                      >
                        <SolidButton
                          startIcon={<AddIcon />}
                          width="80px"
                          height="30px"
                          disabled={cloneForm.ntpServers.length >= 3}
                          onClick={handleAddNtpServer}
                        >
                          Add
                        </SolidButton>
                      </Box>

                      <Typography sx={cloneHelperStyle}>
                        Add up to three servers. The first server is required
                        and cannot be deleted.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        mb: "14px",
                      }}
                    >
                      <Typography sx={cloneLabelStyle}>Address</Typography>

                      <CloneInputField
                        value={cloneForm.address}
                        onChange={(value) =>
                          handleCloneFormChange(
                            "address",

                            value,
                          )
                        }
                      />

                      <Typography sx={cloneHelperStyle}>
                        Copied from {selectedCloneSite.name}. This value is
                        applied to every device the site manages — a new
                        location usually needs a new address.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          ...cloneLabelStyle,

                          mb: "3px",
                        }}
                      >
                        Device Account
                      </Typography>

                      <Typography
                        sx={{
                          ...cloneHelperStyle,

                          mt: 0,

                          mb: "7px",
                        }}
                      >
                        Site-wide credentials every device uses to join. Copied
                        from source — editable.
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",

                          gridTemplateColumns: "1fr 1fr",

                          gap: "12px",
                        }}
                      >
                        <Box>
                          <Typography sx={cloneLabelStyle}>
                            Username *
                          </Typography>

                          <CloneInputField
                            value={cloneForm.username}
                            maxLength={32}
                            isValid={!usernameError}
                            errorHelperText={usernameError}
                            onChange={(value) => {
                              handleCloneFormChange("username", value);

                              const error = validateUsername(value);

                              setUsernameError(error);

                              if (cloneForm.password) {
                                setPasswordError(
                                  validatePassword(cloneForm.password, value),
                                );
                              }
                            }}
                          />
                        </Box>

                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "5px" }}>
                            <Typography sx={{ ...cloneLabelStyle, mb: 0 }}>
                              Password *
                            </Typography>
                            <Tooltip
                              title={passwordRequirementsTip}
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 360,
                                    p: "12px 14px",
                                    bgcolor: "#5f5f5f",
                                  },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: 16,
                                  ml: "5px",
                                  "& img": { content: `url(${infoTipIcon})` },
                                  "&:hover img": { content: `url(${infoTipHoverIcon})` },
                                }}
                              >
                                <img src={infoTipIcon} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                              </Box>
                            </Tooltip>
                          </Box>

                          <CloneInputField
                            type="password"
                            value={cloneForm.password}
                            maxLength={30}
                            isValid={!passwordError}
                            errorHelperText={passwordError}
                            onChange={(value) => {
                              handleCloneFormChange("password", value);

                              const error = validatePassword(
                                value,
                                cloneForm.username,
                              );

                              setPasswordError(error);
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {cloneStep === 2 && (
              <Box
                sx={{
                  px: "20px",

                  pb: "20px",
                }}
              >
                <Typography
                  sx={{
                    color: "#7D8492",

                    fontSize: "13px",

                    lineHeight: "18px",

                    mb: "14px",
                  }}
                >
                  Ready to create your new site? Review the summary, confirm the
                  device password, then finish.
                </Typography>

                <Box
                  sx={{
                    border: "1px solid #DDE1EA",

                    borderRadius: "6px",

                    bgcolor: "#FBFCFE",

                    px: "16px",

                    py: "14px",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",

                      gridTemplateColumns: "150px minmax(0, 1fr)",

                      rowGap: "5px",

                      columnGap: "10px",
                    }}
                  >
                    <Typography sx={summaryLabelStyle}>
                      Creating new site:
                    </Typography>

                    <Typography sx={summaryValueStyle}>
                      "{cloneForm.newSiteName}"
                    </Typography>

                    <Typography sx={summaryLabelStyle}>Cloned from:</Typography>

                    <Typography sx={summaryValueStyle}>
                      "{selectedCloneSite?.name || "-"}"
                    </Typography>

                    <Typography sx={summaryLabelStyle}>
                      Country / Region:
                    </Typography>

                    <Typography sx={summaryValueStyle}>
                      {selectedCountryName}
                    </Typography>

                    <Typography sx={summaryLabelStyle}>Time Zone:</Typography>

                    <Typography sx={summaryValueStyle}>
                      {selectedTimezoneName}
                    </Typography>

                    <Typography sx={summaryLabelStyle}>
                      NTP Server 1:
                    </Typography>

                    <Typography sx={summaryValueStyle}>
                      {cloneForm.ntpServers[0] || "-"}
                    </Typography>

                    <Typography sx={summaryLabelStyle}>
                      NTP Server 2:
                    </Typography>

                    <Typography sx={summaryValueStyle}>
                      {cloneForm.ntpServers[1] || "-"}
                    </Typography>

                    {cloneForm.ntpServers[2] && (
                      <>
                        <Typography sx={summaryLabelStyle}>
                          NTP Server 3:
                        </Typography>

                        <Typography sx={summaryValueStyle}>
                          {cloneForm.ntpServers[2]}
                        </Typography>
                      </>
                    )}

                    <Typography sx={summaryLabelStyle}>Address:</Typography>

                    <Typography sx={summaryValueStyle}>
                      {cloneForm.address || "-"}
                    </Typography>

                    <Typography sx={summaryLabelStyle}>
                      Device Account:
                    </Typography>

                    <Typography sx={summaryValueStyle}>
                      {cloneForm.username || "-"}
                    </Typography>

                    <Typography sx={summaryLabelStyle}>
                      Device Password:
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        gap: "5px",
                      }}
                    >
                      <Typography
                        sx={{
                          ...summaryValueStyle,

                          letterSpacing: showClonePassword ? "normal" : "2px",
                        }}
                      >
                        {showClonePassword ? cloneForm.password : "••••••••"}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => setShowClonePassword((prev) => !prev)}
                        sx={{
                          p: "2px",
                        }}
                      >
                        {showClonePassword ? (
                          <HideIcon
                            sx={{
                              fontSize: "14px",
                            }}
                          />
                        ) : (
                          <ShowIcon
                            sx={{
                              fontSize: "14px",
                            }}
                          />
                        )}
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      height: "1px",

                      bgcolor: "#E5E7EB",

                      my: "16px",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: "14px",

                      fontWeight: 700,

                      color: "#172D6B",

                      mb: "8px",
                    }}
                  >
                    What's coming with it
                  </Typography>

                  {comingWithItems.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        display: "flex",

                        alignItems: "flex-start",

                        gap: "7px",

                        mb: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#14A83B",

                          fontSize: "14px",

                          fontWeight: 700,

                          lineHeight: "18px",
                        }}
                      >
                        ✓
                      </Typography>

                      <Typography
                        sx={{
                          color: "#4D5664",

                          fontSize: "11px",

                          lineHeight: "18px",
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      height: "1px",

                      bgcolor: "#E5E7EB",

                      my: "16px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "14px",

                      fontWeight: 700,

                      color: "#172D6B",

                      mb: "8px",
                    }}
                  >
                    What starts empty
                  </Typography>

                  {startsEmptyItems.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        display: "flex",

                        alignItems: "flex-start",

                        gap: "8px",

                        mb: "7px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#8D95A5",

                          fontSize: "14px",

                          lineHeight: "18px",
                        }}
                      >
                        •
                      </Typography>

                      <Typography
                        sx={{
                          color: "#4D5664",

                          fontSize: "11px",

                          lineHeight: "18px",
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      mt: "14px",

                      px: "12px",

                      py: "10px",

                      display: "flex",

                      alignItems: "flex-start",

                      gap: "8px",

                      bgcolor: "#F0F2FF",

                      borderRadius: "5px",

                      border: "1px solid #E8EAFF",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#41558F",

                        fontSize: "14px",

                        lineHeight: "17px",
                      }}
                    >
                      ↑
                    </Typography>

                    <Typography
                      component="div"
                      sx={{
                        color: "#41558F",

                        fontSize: "11px",

                        lineHeight: "17px",
                      }}
                    >
                      Source Site event history is not copied. This clone
                      creates one new{" "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        Site Cloned
                      </Box>{" "}
                      System Audit event.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {cloneStep === 3 && (
              <Box
                sx={{
                  minHeight: "440px",

                  px: "28px",

                  pt: "55px",

                  pb: "45px",

                  display: "flex",

                  flexDirection: "column",

                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "58px",

                    height: "58px",

                    borderRadius: "50%",

                    bgcolor: "#E7F7EB",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    mb: "18px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#13A33A",

                      fontSize: "34px",

                      fontWeight: 500,

                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: "#172D6B",

                    fontSize: "19px",

                    fontWeight: 700,

                    textAlign: "center",

                    mb: "8px",
                  }}
                >
                  "{cloneCreateResult?.siteName || cloneForm.newSiteName}" is
                  ready!
                </Typography>

                <Typography
                  sx={{
                    color: "#858D9D",

                    fontSize: "13px",

                    lineHeight: "19px",

                    textAlign: "center",

                    mb: "16px",
                  }}
                >
                  The new site is configured successfully. Its first job: add
                  devices.
                </Typography>

                <Box
                  sx={{
                    width: "100%",

                    display: "flex",

                    alignItems: "flex-start",

                    gap: "9px",

                    px: "14px",

                    py: "11px",

                    bgcolor: "#F1F3FF",

                    border: "1px solid #E4E7FF",

                    borderRadius: "6px",

                    mb: "20px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#5167AA",

                      fontSize: "14px",

                      lineHeight: "18px",

                      flexShrink: 0,
                    }}
                  >
                    ℹ
                  </Typography>

                  <Typography
                    component="div"
                    sx={{
                      color: "#40538D",

                      fontSize: "12px",

                      lineHeight: "18px",
                    }}
                  >
                    {createdPaidServiceNames.length > 0 ? (
                      <>
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                          }}
                        >
                          {createdPaidServiceNames.join(", ")}
                        </Box>{" "}
                        was enabled on "
                        {cloneCreateResult?.sourceSiteName ||
                          selectedCloneSite?.name ||
                          "-"}
                        ". It remains disabled here; turn it on from License
                        Management when needed.
                      </>
                    ) : (
                      <>
                        No paid services from the source site are enabled
                        automatically on this new site.
                      </>
                    )}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    gap: "8px",
                  }}
                >
                  <SolidButton height="32px" onClick={handleAddDevices}>
                    Add New Devices
                  </SolidButton>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              px: "20px",

              py: "12px",

              borderTop: "1px solid #E5E7EB",

              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",

              flexShrink: 0,

              bgcolor: "#FFFFFF",
            }}
          >
            {cloneStep === 1 && (
              <>
                <OutlineButton
                  onClick={handleCloseCloneSite}
                  height="34px"
                  width="84px"
                >
                  Cancel
                </OutlineButton>

                <SolidButton
                  disabled={cloneNextDisabled}
                  onClick={handleCloneNext}
                  height="34px"
                  width="84px"
                >
                  Next
                </SolidButton>
              </>
            )}
            {cloneStep === 2 && (
              <>
                <OutlineButton
                  onClick={handleCloneBack}
                  disabled={cloneCreateLoading}
                  height="34px"
                  width="84px"
                >
                  ← Back
                </OutlineButton>

                <SolidButton
                  onClick={handleFlashSite}
                  disabled={cloneCreateLoading}
                  height="34px"
                  width="120px"
                >
                  {cloneCreateLoading ? "Creating..." : "Finish"}
                </SolidButton>
              </>
            )}

            {cloneStep === 3 && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <OutlineButton onClick={handleBackToSiteList} height="34px">
                  Back to Site List
                </OutlineButton>

                <SolidButton
                  onClick={handleViewNewSite}
                  disabled={refreshingSiteList}
                  height="34px"
                >
                  {refreshingSiteList ? "Loading..." : "View New Site"}
                </SolidButton>
              </Box>
            )}
          </Box>
        </Box>
      </ModalContainer>
      <LinkModal
        open={openLinkModal}
        onClose={() => setOpenLinkModal(false)}
        title="Upgrade to Nuclias Unity Pro"
        content="Site Cloning is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to continue."
        linkText="Go to License Management"
        onLinkClick={handleGoToLicenseManagement}
      />
    </>
  );
};

export default Site;
